/**
 * Document to TipTap Conversion Utilities
 * 
 * Converts AeroDocument JSON structure to TipTap editor format.
 * These utilities are shared between StructuredEditor and useDocumentStreaming.
 */

import type { 
  AeroDocument, 
  DocumentSection, 
  ContentBlock,
  ParagraphContent,
  ListContent,
  ListItem,
  TableContent,
  TableColumn,
  TableRow,
  ImageContent,
  HeadingContent,
  CodeBlockContent,
  VectorGraphicContent,
  PlanTaskContent
} from '../types/document-schema';
import { logger } from './logger';

// ===== Section Number Utilities =====
// These derive section metadata from the ID - ID is the source of truth

/**
 * Check if a section ID is hierarchical (sec-1, sec-1-2) vs random (sec-timestamp-random)
 */
export function isHierarchicalSectionId(sectionId: string): boolean {
  if (!sectionId || !sectionId.startsWith('sec-')) return false;
  
  // Hierarchical IDs have short numeric parts: sec-1, sec-1-2, sec-3-2-1
  // Random IDs have long alphanumeric parts: sec-1764019648651-0510nfbqg
  const parts = sectionId.replace('sec-', '').split('-');
  
  // Check if all parts are short numbers (1-3 digits) or single letters (appendix)
  return parts.every(part => /^[0-9]{1,3}$/.test(part) || /^[a-z]$/i.test(part));
}

/**
 * Derive section number from section ID (for hierarchical IDs only)
 * 
 * For hierarchical IDs (sec-1, sec-1-2):
 *   sec-1 → "1"
 *   sec-1-1 → "1.1"
 *   sec-3-2-1 → "3.2.1"
 *   sec-a → "A" (appendix)
 *   sec-a-1 → "A.1"
 * 
 * For random IDs (sec-timestamp-random):
 *   Returns null - caller should use section.number field
 */
export function getSectionNumberFromId(sectionId: string): string | null {
  if (!sectionId) return null;
  
  // Only derive from hierarchical IDs
  if (!isHierarchicalSectionId(sectionId)) {
    return null;
  }
  
  // Handle appendix sections (sec-a, sec-b, etc.)
  const appendixMatch = sectionId.match(/^sec-([a-z])(-.*)?$/i);
  if (appendixMatch) {
    const letter = appendixMatch[1].toUpperCase();
    const rest = appendixMatch[2];
    if (rest) {
      // sec-a-1-2 → A.1.2
      return letter + rest.replace(/-/g, '.').replace(/^\./, '.');
    }
    return letter;
  }
  
  // Handle regular sections (sec-1, sec-1-1, sec-3-2-1)
  return sectionId.replace('sec-', '').replace(/-/g, '.');
}

/**
 * Get the display number for a section
 * Uses ID-derived number for hierarchical IDs, falls back to section.number for random IDs
 */
export function getDisplaySectionNumber(section: { id: string; number?: string }): string {
  const derivedNumber = getSectionNumberFromId(section.id);
  return derivedNumber || section.number || '';
}

/**
 * Get parent section ID from a section ID
 * 
 * Examples:
 *   sec-1-1 → sec-1
 *   sec-3-2-1 → sec-3-2
 *   sec-1 → null (top-level)
 */
export function getParentSectionId(sectionId: string): string | null {
  if (!sectionId || !sectionId.startsWith('sec-')) return null;
  
  const parts = sectionId.split('-');
  if (parts.length <= 2) return null; // sec-1 has no parent
  
  // Remove last part: sec-1-2-3 → sec-1-2
  return parts.slice(0, -1).join('-');
}

/**
 * Get section depth from ID (1 = top-level, 2 = subsection, etc.)
 */
export function getSectionDepthFromId(sectionId: string): number {
  if (!sectionId || !sectionId.startsWith('sec-')) return 1;
  const parts = sectionId.replace('sec-', '').split('-');
  return parts.length;
}

// ===== TipTap Node Types =====

export interface TipTapNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TipTapNode[];
  marks?: TipTapMark[];
  text?: string;
}

export interface TipTapMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface TipTapDoc {
  type: 'doc';
  content: TipTapNode[];
}

// ===== Document Conversion =====

/**
 * Convert an entire AeroDocument to TipTap JSON format
 */
export function convertDocumentToTipTap(document: AeroDocument): TipTapDoc {
  const content: TipTapNode[] = [];
  
  // Guard against invalid document structure
  if (!document?.body || !Array.isArray(document.body)) {
    logger.error('[convertDocumentToTipTap] Invalid document structure - missing or invalid body:', {
      hasDocument: !!document,
      hasBody: !!document?.body,
      bodyType: typeof document?.body
    });
    // Return empty doc to prevent crash
    return { type: 'doc', content: [] };
  }
  
  // Render sections hierarchically - only process top-level sections
  // Child sections are included via their parent's children array
  const topLevelSections = document.body.filter(s => !s.parent);
  
  for (const section of topLevelSections) {
    renderSectionAndChildren(section, document.body, content);
  }
  
  return {
    type: 'doc',
    content
  };
}

/**
 * Recursively render a section and its children to TipTap nodes
 */
export function renderSectionAndChildren(
  section: DocumentSection,
  allSections: DocumentSection[],
  content: TipTapNode[]
): void {
  // Section heading with ID attribute for scrolling and identification
  // Use section number to derive heading level (authoritative source for hierarchy depth)
  const headingLevel = getHeadingLevelFromSectionNumber(section.number);

  // Get display number - uses ID for hierarchical IDs, falls back to section.number
  const sectionNumber = getDisplaySectionNumber(section);
  
  content.push({
    type: 'heading',
    attrs: { 
      level: headingLevel,
      id: section.id
    },
    content: [
      {
        type: 'text',
        text: `${sectionNumber} ${section.title}`
      }
    ]
  });
  
  // Section blocks
  for (const block of section.blocks) {
    const tipTapNode = convertBlockToTipTap(block);
    if (tipTapNode) {
      content.push(tipTapNode);
    }
  }
  
  // Render children recursively
  if (section.children && section.children.length > 0) {
    for (const childId of section.children) {
      const childSection = allSections.find(s => s.id === childId);
      if (childSection) {
        renderSectionAndChildren(childSection, allSections, content);
      }
    }
  }
}

/**
 * Convert a single section to TipTap nodes (for streaming updates)
 * Does NOT render children - only the section itself
 * 
 * @param section - The section to convert
 * @param options.includeHighlight - Whether to add highlight marks (legacy: highlights all)
 * @param options.operationId - ID for the highlight operation
 * @param options.changedBlockIds - Specific block IDs to highlight (targeted highlighting)
 */
export function sectionToTipTapNodes(
  section: DocumentSection,
  options: { includeHighlight?: boolean; operationId?: string; changedBlockIds?: string[] } = {}
): TipTapNode[] {
  const nodes: TipTapNode[] = [];
  const { includeHighlight = false, operationId, changedBlockIds } = options;
  
  // Determine if we should use targeted highlighting (specific blocks) or full section
  const useTargetedHighlight = changedBlockIds && changedBlockIds.length > 0;
  
  // Section heading - use display number (ID-derived or fallback to section.number)
  // Use section number to derive heading level (authoritative source for hierarchy depth)
  const headingLevel = getHeadingLevelFromSectionNumber(section.number);
  const sectionNumber = getDisplaySectionNumber(section);
  const headingTextNode: TipTapNode = {
    type: 'text',
    text: `${sectionNumber} ${section.title}`
  };
  
  // Highlight heading only if it's in changedBlockIds (for add_section) or if full section highlight
  const shouldHighlightHeading = includeHighlight && (
    !useTargetedHighlight || changedBlockIds?.includes(section.id)
  );
  
  if (shouldHighlightHeading) {
    headingTextNode.marks = [{
      type: 'diffHighlight',
      attrs: { type: 'addition', operationId: operationId || section.id }
    }];
  }
  
  nodes.push({
    type: 'heading',
    attrs: { 
      level: headingLevel,
      id: section.id
    },
    content: [headingTextNode]
  });
  
  // Section blocks - only highlight specific changed blocks if changedBlockIds provided
  for (const block of section.blocks) {
    const shouldHighlightBlock = includeHighlight && (
      !useTargetedHighlight || changedBlockIds?.includes(block.id)
    );
    const tipTapNode = convertBlockToTipTap(block, { 
      includeHighlight: shouldHighlightBlock, 
      operationId 
    });
    if (tipTapNode) {
      nodes.push(tipTapNode);
    }
  }
  
  return nodes;
}

/**
 * Get heading level for section type.
 * Falls back to deriving level from section ID if type is unknown.
 */
export function getSectionHeadingLevel(type: string, sectionId?: string): 1 | 2 | 3 | 4 | 5 | 6 {
  switch (type) {
    case 'section':
      return 1;
    case 'subsection':
      return 2;
    case 'subsubsection':
      return 3;
    case 'appendix':
      return 1;
    default:
      // Derive from section ID if type is unknown or missing
      if (sectionId) {
        return getHeadingLevelFromSectionId(sectionId);
      }
      return 2;
  }
}

/**
 * Get heading level (h1-h6) from section number.
 * Section number (e.g., "1.2.3") is the authoritative source for hierarchy depth.
 * Auto-generated section IDs (sec-{timestamp}-{random}) don't encode depth.
 * 
 * "1" → h1, "1.2" → h2, "1.2.3" → h3, etc.
 * Caps at h6 since HTML only supports h1-h6.
 */
export function getHeadingLevelFromSectionNumber(sectionNumber: string): 1 | 2 | 3 | 4 | 5 | 6 {
  if (!sectionNumber) return 2;
  const parts = sectionNumber.split('.');
  const depth = parts.length;
  // Cap at 6 since HTML only supports h1-h6
  return Math.min(depth, 6) as 1 | 2 | 3 | 4 | 5 | 6;
}

/**
 * Get heading level from section ID (for backward compatibility).
 * Handles both traditional IDs (sec-1-2-3) and auto-generated IDs.
 * For auto-generated IDs, falls back to h2.
 */
export function getHeadingLevelFromSectionId(sectionId: string): 1 | 2 | 3 | 4 | 5 | 6 {
  if (!sectionId || !sectionId.startsWith('sec-')) return 2;
  
  // Check if this is an auto-generated ID (sec-{timestamp}-{random})
  // Auto-generated IDs have a timestamp (13+ digits) as the first part
  const withoutPrefix = sectionId.replace('sec-', '');
  const firstPart = withoutPrefix.split('-')[0];
  
  // If first part is a long number (timestamp), this is auto-generated - can't determine depth
  if (firstPart.length > 5) {
    return 2; // Default to h2 for auto-generated IDs
  }
  
  // Traditional ID: sec-1-2-3 → depth from part count
  const parts = withoutPrefix.split('-');
  const depth = parts.length;
  return Math.min(depth, 6) as 1 | 2 | 3 | 4 | 5 | 6;
}

/**
 * Get section level from section ID
 * sec-1 -> 1, sec-1-2 -> 2, sec-1-2-3 -> 3
 */
export function getSectionLevelFromId(sectionId: string): number {
  const parts = sectionId.replace(/^sec-/, '').split('-');
  return parts.length;
}

// ===== Block Conversion =====

/**
 * Convert a content block to TipTap node format
 * 
 * @param block The content block to convert
 * @param options.includeHighlight Whether to add highlight marks
 * @param options.operationId Optional operation ID for tracking
 * @param options.displayBlockId Optional display ID (e.g., "1.2.1") for UI display.
 *        If provided, used instead of internal block.id in data-block-id attribute.
 */
export function convertBlockToTipTap(
  block: ContentBlock,
  options: { includeHighlight?: boolean; operationId?: string; displayBlockId?: string } = {}
): TipTapNode | null {
  const { includeHighlight = false, operationId, displayBlockId } = options;
  
  // Use display ID if provided, otherwise use internal ID
  const blockIdForDisplay = displayBlockId || block.id;
  
  // Helper to add highlight marks to text nodes
  const addHighlightMarks = (textNode: TipTapNode): TipTapNode => {
    if (!includeHighlight) return textNode;
    return {
      ...textNode,
      marks: [
        ...(textNode.marks || []),
        {
          type: 'diffHighlight',
          attrs: { type: 'addition', operationId: operationId || block.id }
        }
      ]
    };
  };

  switch (block.type) {
    case 'paragraph': {
      const paragraphContent = block.content as ParagraphContent | undefined;
      const paragraphText = paragraphContent?.text || '';
      if (!paragraphText.trim()) {
        return null; // Skip empty paragraphs
      }
      
      // Handle quote-styled paragraphs as blockquotes
      if (paragraphContent?.style === 'quote') {
        return {
          type: 'blockquote',
          attrs: { 'data-block-id': blockIdForDisplay, 'data-internal-id': block.id },
          content: [{
            type: 'paragraph',
            content: [addHighlightMarks({ type: 'text', text: paragraphText })]
          }]
        };
      }
      
      return {
        type: 'paragraph',
        attrs: { 'data-block-id': blockIdForDisplay, 'data-internal-id': block.id },
        content: [addHighlightMarks({ type: 'text', text: paragraphText })]
      };
    }
    
    case 'bulleted_list': {
      const listContent = block.content as ListContent | undefined;
      const items = listContent?.items || [];
      return {
        type: 'bulletList',
        attrs: { 'data-block-id': blockIdForDisplay, 'data-internal-id': block.id },
        // Filter out empty items and ensure non-empty text (TipTap rejects empty text nodes)
        content: items
          .filter((item: ListItem) => item.text?.trim())
          .map((item: ListItem) => ({
            type: 'listItem',
            content: [{
              type: 'paragraph',
              content: [addHighlightMarks({ type: 'text', text: item.text || ' ' })]
            }]
          }))
      };
    }
    
    case 'numbered_list': {
      const numberedListContent = block.content as ListContent | undefined;
      const numberedItems = numberedListContent?.items || [];
      return {
        type: 'orderedList',
        attrs: { 'data-block-id': blockIdForDisplay, 'data-internal-id': block.id },
        // Filter out empty items and ensure non-empty text (TipTap rejects empty text nodes)
        content: numberedItems
          .filter((item: ListItem) => item.text?.trim())
          .map((item: ListItem) => ({
            type: 'listItem',
            content: [{
              type: 'paragraph',
              content: [addHighlightMarks({ type: 'text', text: item.text || ' ' })]
            }]
          }))
      };
    }
    
    case 'table': {
      const tableContent = block.content as TableContent | undefined;
      const columns = tableContent?.columns || [];
      const rows = tableContent?.rows || [];
      
      // Helper to safely get cell value - handles both formats:
      // 1. Direct string: row.cells[col.id] = "value"
      // 2. Object format: row.cells[col.id] = { value: "value" }
      const getCellValue = (row: TableRow, colId: string): string => {
        const cell = row.cells?.[colId];
        if (!cell) return ' '; // TipTap requires non-empty text
        if (typeof cell === 'string') return cell || ' ';
        if (typeof cell === 'object' && 'value' in cell) return (cell as { value: string }).value || ' ';
        return ' ';
      };
      
      // Helper to get column header - handles both 'label' and 'header' fields
      const getColumnHeader = (col: TableColumn): string => {
        return col.label || col.header || ' ';
      };
      
      return {
        type: 'table',
        attrs: { 'data-block-id': blockIdForDisplay, 'data-internal-id': block.id },
        content: [
          // Header row
          {
            type: 'tableRow',
            attrs: { 'data-row-id': '__header__' },
            content: columns.map((col: TableColumn) => ({
              type: 'tableHeader',
              attrs: { 'data-col-id': col.id },
              content: [{
                type: 'paragraph',
                content: [{ type: 'text', text: getColumnHeader(col) }]
              }]
            }))
          },
          // Data rows
          ...rows.map((row: TableRow) => ({
            type: 'tableRow',
            attrs: { 'data-row-id': row.id },
            content: columns.map((col: TableColumn) => ({
              type: 'tableCell',
              attrs: { 'data-col-id': col.id },
              content: [{
                type: 'paragraph',
                content: [addHighlightMarks({ 
                  type: 'text', 
                  text: getCellValue(row, col.id)
                })]
              }]
            }))
          }))
        ]
      };
    }
    
    case 'image': {
      const imageContent = block.content as ImageContent | undefined;
      return {
        type: 'image',
        attrs: {
          src: imageContent?.url || imageContent?.s3Key || '',
          alt: imageContent?.alt || '',
          title: imageContent?.caption || ''
        }
      };
    }
    
    case 'heading': {
      const headingContent = block.content as HeadingContent | undefined;
      const headingText = headingContent?.text || '';
      if (!headingText.trim()) {
        return null;
      }
      return {
        type: 'heading',
        attrs: { 
          level: headingContent?.level || 2,
          'data-block-id': blockIdForDisplay,
          'data-internal-id': block.id
        },
        content: [addHighlightMarks({ type: 'text', text: headingText })]
      };
    }
    
    case 'code_block': {
      const codeContent = block.content as CodeBlockContent | undefined;
      return {
        type: 'codeBlock',
        attrs: { 
          language: codeContent?.language || 'text',
          'data-block-id': blockIdForDisplay,
          'data-internal-id': block.id
        },
        content: [{ type: 'text', text: codeContent?.code || '' }]
      };
    }
    
    case 'vector_graphic': {
      const vectorContent = block.content as VectorGraphicContent | undefined;
      return {
        type: 'vectorGraphic',
        attrs: {
          'data-block-id': blockIdForDisplay,
          'data-internal-id': block.id,
          format: vectorContent?.format || 'mermaid',
          content: vectorContent?.content || '',
          caption: vectorContent?.caption || null,
          figureNumber: vectorContent?.figureNumber || null,
          diagramId: vectorContent?.diagramId || null,
          diagramVersion: vectorContent?.diagramVersion || null,
          diagramTitle: vectorContent?.diagramTitle || null,
        }
      };
    }
    
    case 'plan_task': {
      const taskContent = block.content as PlanTaskContent | undefined;
      return {
        type: 'planTask',
        attrs: {
          'data-block-id': blockIdForDisplay,
          'data-internal-id': block.id,
          description: taskContent?.description || '',
          status: taskContent?.status || 'pending',
          order: taskContent?.order || 0,
          targetDocumentName: taskContent?.targetDocumentName || null,
          targetDocumentId: taskContent?.targetDocumentId || null,
          result: taskContent?.result || null,
          error: taskContent?.error || null,
        }
      };
    }
    
    case 'test_result': {
      const testContent = block.content as Record<string, unknown> | undefined;
      const status = (testContent?.status as string) || 'unknown';
      const statusIcon = status === 'pass' ? '\u2705' : status === 'fail' ? '\u274C' : '\u2753';
      const procedure = (testContent?.procedure as string) || '';
      const actualResult = (testContent?.actualResult as string) || '';
      const notes = (testContent?.notes as string) || '';

      const textParts: Array<{ type: string; text: string; marks?: Array<{ type: string }> }> = [
        { type: 'text', text: `${statusIcon} `, marks: [] },
        { type: 'text', text: procedure, marks: [{ type: 'bold' }] },
        { type: 'text', text: ` — ${actualResult}` },
      ];
      if (notes) {
        textParts.push({ type: 'text', text: `\n${notes}` });
      }

      return {
        type: 'paragraph',
        attrs: { 'data-block-id': blockIdForDisplay, 'data-internal-id': block.id },
        content: textParts,
      };
    }

    case 'divider':
      return {
        type: 'horizontalRule'
      };
    
    default:
      // Unknown block type - render as paragraph with warning
      logger.warn('[document-to-tiptap] Unknown block type:', block.type);
      return {
        type: 'paragraph',
        attrs: { 'data-block-id': blockIdForDisplay, 'data-internal-id': block.id },
        content: [{ type: 'text', text: `[Unsupported block type: ${block.type}]` }]
      };
  }
}

// ===== Utility Functions =====

/**
 * Get status badge class for document status
 */
export function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'Released':
    case 'Approved':
      return 'badge-success';
    case 'In Review':
      return 'badge-warning';
    case 'Draft':
      return 'badge-secondary';
    case 'Obsolete':
    case 'Superseded':
      return 'badge-error';
    default:
      return 'badge-info';
  }
}
